import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import {
    Avatar,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Popover,
    Stack,
    SvgIcon,
    Tooltip,
    Typography
} from '@mui/material'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import Scrollbar from '@/components/Scrollbar'
import {
    getNotificationLabel,
    getNotificationUrl
} from '@/features/dashboard/notifications/notificationsUtils'
import { intlDateTimeFormatObjects } from '@/utils/dateTimeFormats'

const renderContent = (item) => {
    const { notification, recipient_state } = item
    const isUnread = !recipient_state?.read_at
    const createdAt = DateTime.fromISO(notification.created_at).toLocaleString(
        intlDateTimeFormatObjects.MONTH_DAYOFMONTH_12TIME
    )
    const actor = notification.actor
    const label = getNotificationLabel(notification)

    return (
        <>
            <ListItemAvatar sx={{ mt: 0.5 }}>
                <Avatar>
                    <SvgIcon>
                        {actor ? (
                            <PersonOutlineOutlinedIcon />
                        ) : (
                            <NotificationsNoneOutlinedIcon />
                        )}
                    </SvgIcon>
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: isUnread ? 700 : 400 }}
                    >
                        {label}
                    </Typography>
                }
                secondary={
                    <Typography color="text.secondary" variant="caption">
                        {createdAt}
                    </Typography>
                }
                sx={{ my: 0 }}
            />
        </>
    )
}

export const NotificationsPopover = (props) => {
    const {
        anchorEl,
        notifications,
        onClose,
        onMarkAllAsRead,
        onRemoveOne,
        open = false,
        ...other
    } = props
    const navigate = useNavigate()

    const isEmpty = notifications.length === 0

    return (
        <Popover
            anchorEl={anchorEl}
            anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom'
            }}
            disableScrollLock
            onClose={onClose}
            open={open}
            {...other}
            slotProps={{
                paper: { sx: { width: 380, maxWidth: 'calc(100vw - 32px)' } }
            }}
        >
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{
                    px: 3,
                    py: 2
                }}
            >
                <Typography color="inherit" variant="h6">
                    Notifications
                </Typography>
                <Stack alignItems="center" direction="row" spacing={1}>
                    <Button
                        onClick={() => {
                            navigate('/notifications')
                            onClose?.()
                        }}
                        size="small"
                    >
                        View all
                    </Button>
                    <Tooltip title="Mark all as read">
                        <IconButton
                            onClick={() => onMarkAllAsRead?.()}
                            size="small"
                            color="inherit"
                            disabled={isEmpty}
                        >
                            <SvgIcon>
                                <DraftsOutlinedIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
            {isEmpty ? (
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                        There are no notifications
                    </Typography>
                </Box>
            ) : (
                <Scrollbar sx={{ maxHeight: 400 }}>
                    <List disablePadding>
                        {notifications.map((item) => {
                            const url = getNotificationUrl(item.notification)
                            return (
                                <ListItem
                                    divider
                                    key={item.notification.id}
                                    onClick={
                                        url
                                            ? () => {
                                                  navigate(url)
                                                  onClose?.()
                                              }
                                            : undefined
                                    }
                                    sx={{
                                        alignItems: 'flex-start',
                                        cursor: url ? 'pointer' : 'default',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        },
                                        '& .MuiListItemSecondaryAction-root': {
                                            top: '24%'
                                        }
                                    }}
                                    secondaryAction={
                                        <Tooltip title="Remove">
                                            <IconButton
                                                edge="end"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    onRemoveOne?.(
                                                        item.notification.id
                                                    )
                                                }}
                                                size="small"
                                            >
                                                <SvgIcon>
                                                    <CloseOutlinedIcon />
                                                </SvgIcon>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    {renderContent(item)}
                                </ListItem>
                            )
                        })}
                    </List>
                </Scrollbar>
            )}
        </Popover>
    )
}

NotificationsPopover.propTypes = {
    anchorEl: PropTypes.any,
    notifications: PropTypes.arrayOf(
        PropTypes.shape({
            notification: PropTypes.shape({
                id: PropTypes.string.isRequired,
                type: PropTypes.string.isRequired,
                verb: PropTypes.string,
                created_at: PropTypes.string.isRequired,
                actor: PropTypes.shape({
                    id: PropTypes.number,
                    display_name: PropTypes.string
                })
            }).isRequired,
            recipient_state: PropTypes.shape({ read_at: PropTypes.string })
        })
    ).isRequired,
    onClose: PropTypes.func,
    onMarkAllAsRead: PropTypes.func,
    onRemoveOne: PropTypes.func,
    open: PropTypes.bool
}
